/**
 * Instruction editor for one visit-note template.
 *
 * Editing is APPEND-ONLY: publishing inserts a new immutable ai_template_version
 * and repoints the template's active pointer. There is no in-place edit and no
 * delete — the DB enforces this with a trigger (trg_ai_template_version_immutable,
 * see astraconnect-admin-console/sql/0001_member_360_schema.up.prod_cutover.sql), so
 * the UI must not imply otherwise.
 *
 * The model dropdown is populated from the server's alias allowlist, never a
 * hardcoded list: an alias the server does not know silently falls back to the
 * default deployment, which would look like "the model setting does nothing".
 *
 * `idPrefix` is required because VisitNotesPage renders FOUR of these on one
 * page. A fixed `id` would produce duplicate DOM ids and point every <label> at
 * the first card's controls.
 */
import { useEffect, useState } from "react";

export interface VisitNotePromptEditorProps {
  /** Unique per rendered instance, e.g. "vn-soap". Namespaces the control ids. */
  idPrefix: string;
  instructions: string;
  /** Model ALIAS from the active version, or null for "server default". */
  model: string | null;
  /** Aliases the server accepts (aiModelAliases). */
  modelAliases: string[];
  saving: boolean;
  onPublish: (args: {
    instructions: string;
    model: string;
    notes: string;
  }) => void | Promise<void>;
}

export function VisitNotePromptEditor({
  idPrefix,
  instructions,
  model,
  modelAliases,
  saving,
  onPublish,
}: VisitNotePromptEditorProps) {
  const [draft, setDraft] = useState(instructions);
  const [draftModel, setDraftModel] = useState(model ?? "");
  const [notes, setNotes] = useState("");

  // Re-sync when the active version changes underneath us (publish / rollback).
  useEffect(() => {
    setDraft(instructions);
    setDraftModel(model ?? "");
    setNotes("");
  }, [instructions, model]);

  const dirty = draft !== instructions || draftModel !== (model ?? "");

  // Publishing is irreversible: ai_template_version blocks UPDATE and DELETE by
  // trigger, so a version with empty instructions can only be SUPERSEDED, never
  // repaired — and while it is active the generation service runs with no rules
  // at all (no "never invent diagnoses", no JSON-shape contract). The server
  // rejects it too (AiTemplateService.publishVersion), but a disabled button and
  // an inline message beat a toast after the fact.
  const emptyInstructions = draft.trim().length === 0;

  // The server's allowlist is authoritative for what you can CHANGE the model
  // to, but it is not authoritative for what the active version already HAS: an
  // alias the seed pinned can be absent from the list (aiModelAliases is empty
  // in every environment today). Without an <option> for it the browser falls
  // back to the first entry, so the card would display "(server default)" for a
  // version that is in fact pinned to 'fast' — and a single click on the only
  // other entry would blank the alias for real.
  const noAliases = modelAliases.length === 0;
  const currentAlias = model ?? "";
  const unlistedAlias =
    currentAlias !== "" && !modelAliases.includes(currentAlias);
  // Nothing to choose between -> no choice to offer. Presenting a one-entry
  // dropdown reads as a decision the admin can make; it is only a way to lose
  // the pinned alias.
  const modelPickerDisabled = saving || noAliases;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label
          htmlFor={`${idPrefix}-instructions`}
          className="block text-sm font-medium mb-1"
        >
          Instructions
        </label>
        <textarea
          id={`${idPrefix}-instructions`}
          rows={14}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full rounded-lg border border-border bg-background p-3 font-mono text-xs disabled:opacity-40 disabled:cursor-not-allowed"
        />
        {emptyInstructions && (
          <p className="m-0 mt-1 text-xs text-destructive">
            Instructions cannot be empty — a published version cannot be edited
            or deleted, only superseded.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div>
          <label
            htmlFor={`${idPrefix}-model`}
            className="block text-sm font-medium mb-1"
          >
            Model
          </label>
          <select
            id={`${idPrefix}-model`}
            value={draftModel}
            disabled={modelPickerDisabled}
            onChange={(e) => {
              // Belt and braces: `disabled` stops a mouse, but not an autofill,
              // a restored form state, or a script. There is no legitimate
              // change to make when the server offered nothing to change to.
              if (modelPickerDisabled) return;
              setDraftModel(e.target.value);
            }}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <option value="">(server default)</option>
            {unlistedAlias && (
              <option value={currentAlias}>
                {currentAlias} (not in this server&apos;s allowlist)
              </option>
            )}
            {modelAliases.map((alias) => (
              <option key={alias} value={alias}>
                {alias}
              </option>
            ))}
          </select>
          {noAliases && (
            <p className="m-0 mt-1 max-w-[18rem] text-[11px] text-muted-foreground">
              This server did not report a model allowlist — the model cannot be
              changed here.
            </p>
          )}
        </div>

        <div className="grow min-w-[12rem]">
          <label
            htmlFor={`${idPrefix}-notes`}
            className="block text-sm font-medium mb-1"
          >
            Change note
          </label>
          <input
            id={`${idPrefix}-notes`}
            type="text"
            value={notes}
            disabled={saving}
            placeholder="Why this change? (stored with the version)"
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          data-testid="publish"
          disabled={!dirty || saving || emptyInstructions}
          onClick={() =>
            void onPublish({ instructions: draft, model: draftModel, notes })
          }
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Publishing..." : "Publish new version"}
        </button>
        <p className="m-0 text-xs text-muted-foreground">
          Publishing creates a new version; previous versions stay available for
          rollback.
        </p>
      </div>
    </div>
  );
}
